import React, { useState, useEffect } from "react";
import api from "../../utils/axios";
import "./auth.css";

const Login = ({ onLogin }) => {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [otpSent, setOtpSent] = useState(false);
  const [resendTimer, setResendTimer] = useState(60);
  const [otp, setOtp] = useState(""); // Move otp state to top level

  useEffect(() => {
    let timer;
    if (otpSent && resendTimer > 0) {
      timer = setInterval(() => {
        setResendTimer((prev) => prev - 1);
      }, 1000);
    } else if (resendTimer === 0) {
      clearInterval(timer);
    }
    return () => clearInterval(timer);
  }, [otpSent, resendTimer]);

  // Handles the initial login attempt (sends credentials, expects OTP to be sent by backend)
  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(""); // Clear previous errors
    try {
      // API call to the backend's /api/login endpoint
      const res = await api.post(`/login`, { email, password });
      if (res.data.success) {
        setOtpSent(true); // If login is successful and OTP is sent, switch to OTP input view
        setResendTimer(60); // Reset timer on successful OTP send
      } else {
        setError(
          res.data.message || "Login failed. Please check your credentials.",
        );
      }
    } catch (err) {
      setError(
        err.response?.data?.message ||
          "Failed to send OTP. Please check your credentials or try again.",
      );
    }
  };

  // Handles OTP verification
  const handleOtpSubmit = async (e) => {
    e.preventDefault();
    setError("");
    try {
      const res = await api.post(`/verify-otp`, { email, otp });
      console.log("OTP verify response:", res.data);
      if (res.data && res.data.success) {
        setError("");
        if (res.data.accessToken) {
          localStorage.setItem("accessToken", res.data.accessToken);
        }
        onLogin(email);
      } else {
        setError(
          (res.data && res.data.message) ||
            "OTP verification failed. Please try again.",
        );
      }
    } catch (err) {
      setError(
        err.response?.data?.message ||
          "OTP verification failed. Please try again.",
      );
    }
  };

  // Handles resending the OTP
  const handleResendOtp = async () => {
    setError(""); // Clear previous errors
    try {
      // API call to the backend's /api/resend-otp endpoint
      const res = await api.post(`/resend-otp`, { email });
      if (res.data.success) {
        setResendTimer(60); // Reset timer on successful resend
        alert("New OTP sent to your email!");
      } else {
        setError(res.data.message || "Failed to resend OTP.");
      }
    } catch (err) {
      setError(err.response?.data?.message || "Failed to resend OTP.");
    }
  };

  return (
    <div className="auth-container">
      {!otpSent ? ( // Show login form if OTP hasn't been sent
        <form onSubmit={handleSubmit} className="auth-form">
          <h2>Login</h2>
          {error && <div className="auth-error">{error}</div>}
          <input
            type="email"
            placeholder="Email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            required
          />
          <div className="password-wrapper">
            <input
              type={showPassword ? "text" : "password"}
              placeholder="Password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              style={{ paddingRight: "2.2rem" }}
            />
            <span
              className="show-hide-icon"
              onClick={() => setShowPassword((prev) => !prev)}
              tabIndex={0}
              aria-label={showPassword ? "Hide password" : "Show password"}
              role="button"
              style={{
                position: "absolute",
                right: "0.8rem",
                top: "50%",
                transform: "translateY(-50%)",
                cursor: "pointer",
                color: "#888",
                fontSize: "1.2rem",
              }}
            >
              <i className={`bx ${showPassword ? "bx-hide" : "bx-show"}`}></i>
            </span>
          </div>
          <button type="submit">Send OTP</button>
        </form>
      ) : (
        // Show OTP verification form if OTP has been sent
        <form onSubmit={handleOtpSubmit} className="auth-form">
          <h2>Enter OTP</h2>
          <input
            type="text"
            placeholder="Enter 6-digit OTP"
            value={otp}
            onChange={(e) => setOtp(e.target.value)}
            required
            maxLength={6}
          />
          <button type="submit">Verify OTP</button>
          <button
            type="button"
            className="resend-otp-btn"
            onClick={handleResendOtp}
            disabled={resendTimer > 0}
          >
            {resendTimer > 0 ? `Resend OTP in ${resendTimer}s` : "Resend OTP"}
          </button>
          {error && <div className="auth-error">{error}</div>}
        </form>
      )}
    </div>
  );
};

export default Login;
